<template>
    <div class="widget-component">
        <v-container fluid id="main-frame">
            <v-progress-linear
                :active="loading"
                :indeterminate="loading"
                absolute
                top
                color="primary"
            ></v-progress-linear>
            <v-overlay :value="loading">
                <v-progress-circular
                    indeterminate
                    size="64"
                    color="primary"
                ></v-progress-circular>
            </v-overlay>

            <ListingHeader
                class="bg-gray-f1f3f5"
                :listing="listing"
                :eligibilityLink="eligibilityLink"
                :options="settings"
            ></ListingHeader>

            <v-container class="container-width" id="vcontainer-gallery">
                <v-row>
                    <v-col cols="12">
                        <a :href="computedSearchUrl" id="back-to-search"
                           class="color-primary text-decoration-none vd-none vd-md-inline-block">
                            <v-icon color="primary">mdi-chevron-left</v-icon>
                            Back to search
                        </a>

                        <ListingGallery :listing="listing"></ListingGallery>
                    </v-col>
                </v-row>
            </v-container>

            <v-container class="container-width">
                <v-row>
                    <v-col v-if="listing.availableSchemes && listing.availableSchemes.length > 0" class="vd-sm-none" cols="12" align-self="center">
                        <SchemeSwitcher :listing="listing"></SchemeSwitcher>
                    </v-col>

                    <v-col cols="12" class="mb-5" v-if="listing.logo && listing.logo.url">
                        <h1>{{ listing.headline }}</h1>
                    </v-col>

                    <v-col cols="12">
                        <v-row>
                            <v-col cols="12" sm="12" md="8">
                                <LongDescription v-if="listing.longDescription"
                                                 :description="listing.longDescription"
                                                 :charLimit="550"></LongDescription>

                                <ListingAgentBox
                                    v-if="listing.estateAgency"
                                    :agent="listing.estateAgency"
                                    :settings="has(settings, 'agentBox') ? settings.agentBox : null"
                                >
                                </ListingAgentBox>

                                <LocalAreaDescription class="mt-10" v-if="listing.localAreaDescription"
                                                      :description="listing.localAreaDescription"
                                                      :charLimit="550"></LocalAreaDescription>

                                <div v-if="listing.singleProperty">
                                    <SinglePropertyFeatures :listing="listing"></SinglePropertyFeatures>
                                </div>

                            </v-col>
                            <v-col cols="12" sm="12" md="4">
                                <!-- <Faq v-if="faq" :faq="faq"></Faq> -->

                                <v-card elevation="10" class="px-5 py-6">
                                    <v-row>
                                        <v-col cols="12" v-if="showBookingForm">
                                            <v-btn
                                                href="#book-viewing"
                                                color="primary"
                                                outlined
                                                block
                                                large
                                            >
                                                Book a viewing
                                            </v-btn>
                                        </v-col>

                                        <v-col cols="12" v-if="listing.videos && listing.videos.length > 0">
                                            <v-btn
                                                href="#virtual-tour"
                                                color="primary"
                                                outlined
                                                block
                                                large
                                            >
                                                Virtual tour
                                            </v-btn>
                                        </v-col>

                                        <v-col cols="12" v-for="(brochure, index) in listing.brochures" :key="index">
                                            <v-btn
                                                :href="brochure.url"
                                                :title="brochure.caption"
                                                download
                                                color="primary"
                                                outlined
                                                block
                                                large
                                            >
                                                Download {{ brochure.captionIsFileName && brochure.caption ? brochure.caption : 'brochure' }}
                                            </v-btn>
                                        </v-col>

                                        <v-col cols="12">
                                            <v-btn
                                                href="#register"
                                                color="primary"
                                                :elevation="0"
                                                block
                                                large
                                            >
                                                Register interest
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card>

                                <v-btn
                                    color="primary"
                                    block
                                    text
                                    class="mt-4"
                                    @click="termsAndConditionsDialog.show = true">
                                    Terms & Conditions
                                </v-btn>

                                <v-btn
                                    color="primary"
                                    block
                                    text
                                    class="mt-4"
                                    @click="disclaimerDialog.show = true">
                                    Disclaimer
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" sm="12" md="8" class="mb-5">
                        <a
                            @click.prevent="openMapDialog"
                            href="#"
                            class="vd-flex pb-8"
                        >
                            <v-sheet
                                :height="$vuetify.breakpoint.mdAndUp ? 520 : 'auto'"
                                width="100%"
                            >
                                <v-img
                                    width="100%"
                                    max-width="550"
                                    :src="listing.mapImageUrl"
                                ></v-img>
                            </v-sheet>
                        </a>
                        <div class="vd-block vd-md-none text-right">
                            <v-btn
                                color="primary"
                                class="white--text"
                                @click.prevent="openMapDialog"
                            >
                                <v-icon left>
                                    mdi-map-outline
                                </v-icon>
                                Map
                            </v-btn>
                        </div>
                    </v-col>
                </v-row>
            </v-container>

            <ListingSpecifications v-if="listing.specifications && listing.specifications.length > 0"
                                   :specifications="listing.specifications"></ListingSpecifications>

            <v-container class="container-width py-10" v-if="settings.eligibilityCalculator && settings.eligibilityCalculator.shown && listing.isSharedOwnership && listing.newBuild">
                <v-row class="my-5">
                    <v-col cols="12">
                        <h2 class="color-primary mb-4">{{ settings.eligibilityCalculator.title }}</h2>
                        <div v-html="settings.eligibilityCalculator.text"></div>
                    </v-col>
                </v-row>

                <budget-calculator
                    :properties="filteredProperties"
                    :default-full-value="settings.eligibilityCalculator.props.defaultFullValue"
					:default-share-percentage="settings.eligibilityCalculator.props.defaultSharePercentage"
					:interest="settings.eligibilityCalculator.props.interest"
					:term="settings.eligibilityCalculator.props.term"
                    :options="settings.eligibilityCalculator.props.options"
                    v-if="listing.isSharedOwnership"
                ></budget-calculator>
            </v-container>

            <listing-videos
                id="virtual-tour"
                :listing="listing"
                :settings="{sliderType: 'slideGroup'}"
            ></listing-videos>

            <v-container class="container-width">
                <ListingAvailability
                    id="availability"
                    v-if="filteredProperties && filteredProperties.length > 0"
                    :forSale="listing.forSale"
                    :isSharedOwnership="listing.isSharedOwnership"
                    :schemeGroup="listing.schemeGroup.slug"
                    :bedrooms="listing.bedrooms"
                    :listing="listing"
                    :properties="filteredProperties"
                    :currency-formatter="currencyFormatter"
                    :settings="has(settings, 'availabilityTable') ? settings.availabilityTable : null "
                >
                </ListingAvailability>


                <!-- Book Viewing -->
                <v-row
                    class="py-10"
                    v-if="showBookingForm"
                    id="book-viewing"
                >
                    <v-col cols="12" md="6">
                        <h2 class="color-primary mb-4">Book your viewing</h2>

                        <p v-html="bookingText"></p>
                    </v-col>

                    <v-col cols="12" md="8">
                        <booking-form
                            v-if="showBookingForm"
                            inline
                            :modal="false"
                            :listing-id="listing.id"
                            :listing-name="listing.name"
                            :listing-location="listing.displayAddress"
                            :logged-in-profile="loggedInProfile"
                            :prefill-from-customer-object="customerObject"
                            @booked="userBooked = true"
                            @showLoginDialog="loginDialog = true"
                            @refreshBooking="bookingFormRender++"
                            :key="bookingFormRender"
                        ></booking-form>
                    </v-col>
                </v-row>
            </v-container>

            <eligibility-form
              v-if="listing.slug && !userBooked"
              id="register"
              class="bg-gray-f1f3f5 py-10"
              :root-url="rootUrl"
              :listing-slug="listing.slug"
              :access-token="accessToken"
              :housing-provider-name="listing.housingProvider.name"
              :settings="settings.eligibilityForm"
              :email-verification-url="emailVerificationUrl"
              @registerInterestFormSubmitted="registerInterestFormSubmitted"
            >
            </eligibility-form>
        </v-container>

        <v-dialog
            v-model="specificationDialog.show"
            fullscreen
            hide-overlay
            transition="dialog-bottom-transition"
        >
            <v-card class="widget-component" height="100%">
                <v-container class="container-width">
                    <v-row>
                        <v-col cols="10">
                            <h1>{{ listing.headline }}</h1>
                            <p>
                                <span v-if="listing.displayAddress">{{ listing.displayAddress }}</span>
                                <span v-if="listing.postcode">, {{ listing.postcode }}</span>
                                <v-chip label v-if="listing.availabilityStatus" small class="ma-2">
                                    {{ listing.availabilityStatus.name }}
                                </v-chip>
                                <v-chip label v-if="listing.newBuild" small class="ma-2">New home</v-chip>
                            </p>
                        </v-col>
                        <v-col cols="2" class="text-right">
                            <v-btn
                                large
                                icon
                                @click="closeSpecificationDialog"
                            >
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" v-if="selectedProperty && selectedProperty.floorplans">
                            <div class="plans-container">
                                <v-row justify="center">
                                    <a
                                        :href="floorplan.url"
                                        v-for="(floorplan, index) in selectedProperty.floorplans"
                                        :key="index"
                                    >
                                        <v-img :src="floorplan.url">
                                            <template v-slot:placeholder>
                                                <v-row
                                                    class="fill-height ma-0"
                                                    align="center"
                                                    justify="center"
                                                >
                                                    <v-progress-circular
                                                        indeterminate
                                                        color="grey lighten-5"
                                                    ></v-progress-circular>
                                                </v-row>
                                            </template>
                                        </v-img>
                                    </a>
                                </v-row>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="selectedProperty && selectedProperty.fullSpecifications">
                        <v-col cols="12">
                            <div class="specification-container">
                                <div v-html="selectedProperty.fullSpecifications"></div>
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card>
        </v-dialog>

        <v-dialog
            v-model="videoDialog.show"
            fullscreen
            hide-overlay
            transition="dialog-bottom-transition"
        >
            <v-card class="widget-component" height="100%">
                <v-container class="container-width">
                    <v-row>
                        <v-col cols="10">
                            <h1>{{ listing.headline }}</h1>
                            <p>
                                <span v-if="listing.displayAddress">{{ listing.displayAddress }}</span>
                                <span v-if="listing.postcode">, {{ listing.postcode }}</span>
                                <v-chip label v-if="listing.availabilityStatus" small class="ma-2">
                                    {{ listing.availabilityStatus.name }}
                                </v-chip>
                                <v-chip label v-if="listing.newBuild" small class="ma-2">New home</v-chip>
                            </p>
                        </v-col>
                        <v-col cols="2" class="text-right">
                            <v-btn
                                large
                                icon
                                @click="closeVideoDialog"
                            >
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" v-if="videoDialog.embed">
                            <div class="iframe-container" v-html="videoDialog.embed">
                            </div>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="mapDialog.show"
            fullscreen
            hide-overlay
            transition="dialog-bottom-transition"
        >
            <v-card class="widget-component" height="50vh" id="map-dialog">
                <v-container class="container-width" ref="mapDialogHeader">
                    <v-row>
                        <v-col cols="10">
                            <h1 class="color-primary mt-3">{{ listing.headline }}</h1>
                            <p class="mb-1">
                                <span v-if="listing.displayAddress">{{ listing.displayAddress }}</span>
                                <span v-if="listing.postcode">, {{ listing.postcode }}</span>
                                <v-chip label v-if="listing.availabilityStatus" small class="ma-2">
                                    {{ listing.availabilityStatus.name }}
                                </v-chip>
                                <v-chip label v-if="listing.newBuild" small class="ma-2">New home</v-chip>
                            </p>
                        </v-col>
                        <v-col cols="2" class="text-right">
                            <v-btn
                                large
                                icon
                                @click="closeMapDialog"
                            >
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-tabs
                            v-model="mapDialogTab"
                            right
                            slider-color="primary"
                        >
                            <v-tab href="#map">Map</v-tab>
                            <v-tab href="#streetview">Street View</v-tab>
                            <v-tab href="#info">Info</v-tab>
                        </v-tabs>
                    </v-row>
                </v-container>
                <v-tabs-items v-model="mapDialogTab" class="mt-5" v-bind:style="{ height: tabsItemsHeight + 'px' }">
                    <v-tab-item value="map" class="fill-height">
                        <v-row class="fill-height">
                            <v-col cols="12" class="fill-height">
                                <v-card flat class="fill-height">
                                    <GmapMap
                                        :center="listingCoordinates"
                                        :zoom="16"
                                        map-type-id="terrain"
                                        ref="map"
                                        style="width: 100%; height: 100%"
                                    >
                                        <GmapMarker
                                            :position="listingCoordinates"
                                        />
                                    </GmapMap>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                    <v-tab-item value="streetview" class="fill-height">
                        <v-row class="fill-height">
                            <v-col cols="12" class="fill-height">
                                <v-card flat class="fill-height">
                                    <gmap-street-view-panorama
                                        class="pano"
                                        :position="listingCoordinates"
                                        :zoom="1"
                                        style="width: 100%; height: 100%"
                                    >
                                    </gmap-street-view-panorama>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>
                    <v-tab-item value="info">
                        <v-container class="container-width">
                            <v-card flat>
                                <div class="pt-4 pb-4" v-html="listing.localAreaDescription"></div>
                            </v-card>
                        </v-container>
                    </v-tab-item>
                </v-tabs-items>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="disclaimerDialog.show"
            max-width="800"
        >
            <v-card class="widget-component py-4">
                <v-container class="container-width">
                    <v-row>
                        <v-col cols="12">
                            <h2 class="color-primary">Disclaimer</h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" v-if="listing.disclaimer">
                            <div v-html="listing.disclaimer"></div>
                        </v-col>
                    </v-row>
                    <v-divider></v-divider>

                        <v-card-actions>
                        <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                text
                                @click="disclaimerDialog.show = false"
                            >
                                Close
                            </v-btn>
                        </v-card-actions>
                </v-container>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="termsAndConditionsDialog.show"
            max-width="800"
        >
            <v-card class="widget-component py-4">
                <v-container class="container-width">
                    <v-row>
                        <v-col cols="12">
                            <h2 class="color-primary">Terms & Conditions</h2>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" v-if="listing.termsAndConditions">
                            <div v-html="listing.termsAndConditions"></div>
                        </v-col>
                        <v-divider></v-divider>

                        <v-card-actions>
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                text
                                @click="termsAndConditionsDialog.show = false"
                            >
                                Close
                            </v-btn>
                        </v-card-actions>
                    </v-row>
                </v-container>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import {defineComponent, ref, onMounted, computed, watch, nextTick} from '@vue/composition-api';
import ListingHeader from "./partials/ListingHeader.vue";
import ListingGallery from './partials/ListingGallery.vue';
import LongDescription from "./partials/LongDescription.vue";
import LocalAreaDescription from "./partials/LocalAreaDescription.vue";
import ListingSpecifications from './partials/ListingSpecifications.vue';
import BudgetCalculator from './partials/BudgetCalculator.vue';
import ListingAvailability from './partials/ListingAvailability.vue';
import ListingVideos from "./ListingVideos.vue";
import Faq from "./partials/Faq.vue";
import SchemeSwitcher from './partials/micro/listing/SchemeSwitcher.vue';
import SinglePropertyFeatures from "./partials/SinglePropertyFeatures.vue";
import ListingAgentBox from "./ListingAgentBox.vue";
import isMobile from '../composables/isMobile';
import sendMessageToParent from '../composables/sendMessageToParent';
import getCookie from '../composables/getCookie';
import _, {filter, forEach, has, includes, inRange, isEmpty, isNil, isNumber, join, map, minBy, omitBy} from 'lodash';
import ListingService from "../services/ListingService";

export default defineComponent({
    name: 'ListingDetails',
    props: {
        rootUrl: {
            type: String,
            required: true
        },
        searchUrl: {
            type: String,
            required: false
        },
        listingSlug: {
            type: String,
            required: true
        },
        accessToken: {
            type: String,
        },
        faq: {
            type: Object,
        },
        settings: {
            type: Object
        },
        emailVerificationUrl: {
            type: String
        }
    },
    components: {
        ListingHeader,
        ListingGallery,
        LongDescription,
        LocalAreaDescription,
        ListingSpecifications,
        BudgetCalculator,
        ListingAvailability,
        Faq,
        SinglePropertyFeatures,
        ListingVideos,
        ListingAgentBox,
        SchemeSwitcher
    },
    setup(props, context) {
        const token = ref(null);
        const mapDialogHeader = ref(null);
        const loggedInProfile = ref(null);
        const customerObject = ref(null);
        const loginDialog = ref(false);
        const loading = ref(false);
        const listing = ref({});
        const checkEligibility = ref(null);
        const properties = ref([]);
        const orderedProperties = ref([]);
        const selectedPropertyTypes = ref([]);
        const propertyBedroomTypes = ref([]);
        const specificationDialog = ref({
            show: false,
        });
        const selectedProperty = ref(null);
        const mapDialog = ref({
            show: false,
            defaultTab: 'map'
        });
        const videoDialog = ref({
            show: false,
            embed: null,
        });
        const disclaimerDialog = ref({
            show: false
        });
        const termsAndConditionsDialog = ref({
            show: false
        });

        const hasUpcomingOpenDay = ref(false);
        const userBooked = ref(false);

        const bookingFormRender = ref(0);
        const tabsItemsHeight = ref(0);

        const filteredProperties = computed(() => {
            let props = []
            if (properties.value.length > 0) {
                props = filter(properties.value, function (property) {
                    if (includes(selectedPropertyTypes.value, property.bedrooms) || selectedPropertyTypes.value.length == 0) {
                        if (property.availabilityStatus.code == 'available' || property.availabilityStatus.code == 'coming-soon'
                         || property.availabilityStatus.code == 'closed' || property.availabilityStatus.code == 'under-offer') {
                            return true;
                        }
                    }
                });
            } else if (listing.value.singleProperty) {
                props[0] = listing.value.propertyDetails;
                props[0].singleProperty = true;
            }
            return props;
        });

        const mapDialogTab = computed({
            get() {
                if (context.root.$route.query.tab) {
                    return context.root.$route.query.tab;
                }
            },
            set(val) {
                let query = {...context.root.$route.query};
                query.tab = val;
                context.root.$router.replace({query: query});
            },
        });

        const listingCoordinates = computed(() => {
            return {
                lat: listing.value.latitude,
                lng: listing.value.longitude
            }
        });

        const bookingText = computed(() => {
            if(listing.value.enablePrivateOpenDays && props.settings.bookingForm.textOpenDays) {
                return props.settings.bookingForm.textOpenDays
            }

            if(props.settings.bookingForm && props.settings.bookingForm.text){
                return props.settings.bookingForm.text
            }

            return "<p>If no available appointments are shown, please register your interest and look out for an eflyer inviting you to the next available opportunity to view.</p>"
        });

        const computedSearchUrl = computed(() => {
            return props.searchUrl + getCookie('keaze_widgets_search');
        });

        const showBookingForm = computed(() => {
            if(listing.value) {
                if(
                    listing.value.id &&
                    //listing.value.enablePrivateOpenDays === false &&
                    hasUpcomingOpenDay.value === true
                ) {
                    return true;
                }
            }
            return false;
        });

        const specsOptions = computed(() => {
            if(has(props, 'settings.stats')) {
                return props.settings.stats;
            }
            return null;
        });

        const currencyFormatter = new Intl.NumberFormat('gb-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })

        onMounted(() => {
            fetchListing();

            window.addEventListener("resize", ()=>{
                setTabsHeight();
            })
        })

        function setTabsHeight() {
            const mapDialogHeaderHeight = mapDialogHeader.value ? mapDialogHeader.value.clientHeight : 0;
            const windowHeight = window.innerHeight;
            tabsItemsHeight.value = windowHeight - mapDialogHeaderHeight;
        }

        function fetchListing() {
            loading.value = true;
            ListingService.getListingFullDetailsBySlug(props.listingSlug)
                .then(function (response) {
                    if (has(response, 'data.listing.checkEligibility')) {
                        checkEligibility.value = response.data.checkEligibility;
                    }
                    if (has(response, 'data.listing')) {
                        listing.value = response.data.listing;
                        fetchAvailableDates();
                    }
                    if (has(response, 'data.properties')) {
                        properties.value = response.data.properties;
                        initProperties();
                    }
                })
                .catch(function (error) {
                    console.error(error);
                    if (error.response && error.response.status) {
                        if (error.response.status == 404) {
                            window.location = '/404';
                        }
                    }
                })
                .then(function () {
                    loading.value = false;
                });
        }

        function fetchAvailableDates() {
            ListingService.getUpcomingOpenDaysByListingSlug(props.listingSlug)
                .then(function (response) {
                    if (response.data === undefined || response.data.length == 0) {
                        hasUpcomingOpenDay.value = false;
                    } else {
                        hasUpcomingOpenDay.value = true;
                    }
                })
                .catch(function (error) {
                    console.error(error);
                })
        }

        function openMapDialog() {
            mapDialog.value.show = true;
            context.root.$router.push({
                query: {
                    map: 'open'
                }
            });
            let height = window.screen.height;

            setTimeout(() => {
                setTabsHeight();
            }, 0);

        }

        function closeMapDialog() {
            let query = Object.assign({}, context.root.$route.query);
            delete query.map;
            delete query.tab;
            context.root.$router.replace({query});
            mapDialog.value.show = false;
            tabsItemsHeight.value = 0;
        }

        function openVideoDialog(videoIndex) {
            if (!isNil(listing.value.videos[videoIndex].extraDetails.embedCode)) {
                videoDialog.value.embed = listing.value.videos[videoIndex].extraDetails.embedCode;
                videoDialog.value.show = true;
                context.root.$router.push({
                    query: {
                        showvideo: videoIndex
                    }
                });
                let height = window.screen.height;
            }
        }

        function closeVideoDialog() {
            let query = Object.assign({}, context.root.$route.query);
            delete query.showvideo;
            context.root.$router.replace({query});
            videoDialog.value.embed = null;
            videoDialog.value.show = false;
        }

        function openSpecificationDialog(propertyIndex) {
            selectedProperty.value = properties.value[propertyIndex];
            specificationDialog.value.show = true;
            context.root.$router.push({
                query: {
                    showproperty: propertyIndex
                }
            });
            let height = window.screen.height;

        }

        function closeSpecificationDialog() {
            let query = Object.assign({}, context.root.$route.query);
            delete query.showproperty;
            context.root.$router.replace({query});
            specificationDialog.value.embed = null;
            specificationDialog.value.show = false;
        }

        function initProperties() {
            let result = [];
            if (properties.value && properties.value.length > 0) {
                result =
                    _(properties.value)
                        .filter((prop) => {
                            return ['available', 'under-offer'].includes(prop.availabilityStatus.code);
                        })
                        .groupBy('bedrooms')
                        .map((group) => minBy(group, function (o) {
                            if (o.fullMarketPrice) {
                                return o.fullMarketPrice;
                            } else {
                                return 0;
                            }
                        }))
                        .value();
            }
            propertyBedroomTypes.value = result;
        }

        function formattedBedrooms(bedroomsNumber) {
            let returnString = '';
            if (bedroomsNumber == 0) {
                returnString += 'Studio';
            }
            if (bedroomsNumber == 1) {
                returnString += bedroomsNumber + ' bedroom';
            }
            if (bedroomsNumber > 1) {
                returnString += bedroomsNumber + ' bedrooms';
            }
            return returnString;
        }

        function selectPropertyBedroomType(bedroomsNumber) {
            if (!includes(selectedPropertyTypes.value, bedroomsNumber)) {
                selectedPropertyTypes.value.push(bedroomsNumber);
            } else {
                const index = selectedPropertyTypes.value.indexOf(bedroomsNumber);
                if (index > -1) {
                    selectedPropertyTypes.value.splice(index, 1);
                }
                //context.root.$router.replace({ query });
            }
        }

        function propertyBedroomTypeClass(bedroomsNumber) {
            if (includes(selectedPropertyTypes.value, bedroomsNumber)) {
                return 'selected';
            }
            return '';
        }

        function showProperty(bedroomsNumber) {
            if (includes(selectedPropertyTypes.value, bedroomsNumber) || selectedPropertyTypes.value.length == 0) {
                return true;
            }
            return false;
        }

        function planOrSpecificationButtonDisabled(index) {
            if (!isNil(properties.value[index].floorplans) || !isNil(properties.value[index].fullSpecifications)) {
                return false;
            }
            return true;
        }

        function eligibilityLink(propertyNumber) {
            let url = props.rootUrl + '/';

            if (checkEligibility.value === true) {
                url += 'check-eligibility/';
            } else {
                url += 'register-interest/';
            }

            url += props.listingSlug;
            if (isNumber(propertyNumber)) {
                url += '/property/' + propertyNumber;
            }
            return url;
        }

        function showDetailLine(attribute) {
            let a = map(properties.value, attribute);
            let filtered = omitBy(a, isNull);
            return !isEmpty(filtered);
        }

        function getPropertyFeatures(property) {
            let features = [];
            if (property.propertyFeatures && property.propertyFeatures.length > 0) {
                forEach(property.propertyFeatures, function (value) {
                    if (value.values && value.values.length > 0) {
                        forEach(value.values, function (feature) {
                            features.push(feature);
                        });
                    }
                });
            }
            return join(features, '<br />');
        }

        function getEEREIRClass(type, value) {
            let returnClass = '';
            if (isNumber(value)) {
                returnClass += 'bg-' + type + '-';
                if (inRange(value, 1, 20)) {
                    returnClass += '1-20';
                } else if (inRange(value, 21, 38)) {
                    returnClass += '21-38';
                } else if (inRange(value, 39, 54)) {
                    returnClass += '39-54';
                } else if (inRange(value, 55, 68)) {
                    returnClass += '55-68';
                } else if (inRange(value, 69, 80)) {
                    returnClass += '69-80';
                } else if (inRange(value, 81, 91)) {
                    returnClass += '81-91';
                } else if (inRange(value, 92, 100)) {
                    returnClass += '92-100';
                }
            }
            return returnClass;
        }

        function handleLoginEmit(data) {
            if (data.loggedInProfile) {
                loggedInProfile.value = data.loggedInProfile;
            }
            if (data.accessToken) {
                token.value = data.accessToken;
            }

            bookingFormRender.value = bookingFormRender.value + 1;
        }

        function handleLoginDialogEmit(val) {
            loginDialog.value = val;
        }

        function registerInterestFormSubmitted(val) {
            if(has(val,'customer')) {
                customerObject.value = val.customer;
                bookingFormRender.value = bookingFormRender.value + 1;
            }
        }

        return {
            loading,
            computedSearchUrl,
            checkEligibility,
            listing,
            properties,
            mapDialog,
            listingCoordinates,
            mapDialogTab,
            videoDialog,
            bookingText,
            orderedProperties,
            selectedPropertyTypes,
            propertyBedroomTypes,
            currencyFormatter,
            specificationDialog,
            selectedProperty,
            filteredProperties,
            loginDialog,
            hasUpcomingOpenDay,
            userBooked,
            loggedInProfile,
            bookingFormRender,
            token,
            disclaimerDialog,
            termsAndConditionsDialog,
            openMapDialog,
            closeMapDialog,
            openVideoDialog,
            closeVideoDialog,
            formattedBedrooms,
            selectPropertyBedroomType,
            propertyBedroomTypeClass,
            showProperty,
            openSpecificationDialog,
            closeSpecificationDialog,
            planOrSpecificationButtonDisabled,
            eligibilityLink,
            showDetailLine,
            getEEREIRClass,
            getPropertyFeatures,
            sendMessageToParent,
            handleLoginEmit,
            handleLoginDialogEmit,
            isMobile,
            mapDialogHeader,
            tabsItemsHeight,
            registerInterestFormSubmitted,
            customerObject,
            specsOptions,
            showBookingForm,
            has
        }

    }
});
</script>

<style lang="scss">
@import '~vuetify/src/styles/styles.sass';

#back-to-search {
    font-size: 12px;
    line-height: 16px;

    .v-icon {
        vertical-align: sub;
        font-size: 20px;
    }
}

#vcontainer-gallery {
    @media #{map-get($display-breakpoints, 'sm-and-down')}{
        padding: 0px;

        .col {
            padding: 0px;
        }
    };
}

.iframe-container {
    overflow: hidden;
    padding-top: 56.25%;
    position: relative;
}

.iframe-container iframe {
    border: 0;
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
}

.v-card {
    &.propertyBedroomTypeCard {
        border-width: 2px;

        &.selected {
            border: 2px solid var(--v-primary-base);
        }
    }
}

.logo-cell {
    border: 2px solid rgb(241, 243, 245) !important;
}

.scroll-container {
    overflow: auto;
}

.cell {
    background-color: rgb(241, 243, 245);

    .explanation-cell {
        border-bottom: 1px dashed var(--v-primary-base)
    }
}

.availabiltyTable {
    border-collapse: collapse;

    th, td {
        padding: 0;
    }

    .first-column {
        position: sticky;
        left: 0px;
        top: auto;
        z-index: 10;
    }

    .border-right {
        border-right: 1px solid rgb(241, 243, 245);
    }
}

.playIconContainer {
    position: absolute;
    z-index: 100;
}

.sPropRow {
    position: relative;
    display: flex;
    -webkit-box-pack: justify;
    justify-content: space-between;
    -webkit-box-align: center;
    align-items: center;
    margin-bottom: 5px;

    &::after {
        content: "";
        position: absolute;
        bottom: 6px;
        left: 0px;
        width: 100%;
        height: 1px;
        background-color: rgb(48, 48, 48);
        opacity: 0.07;
        z-index: -1;
    }

    .sPropAttr {
        position: relative;
        display: flex;
        color: rgb(48, 48, 48);
        background-color: rgb(241, 243, 245);
        font-size: 0.875rem;
        line-height: 1.375rem;
        font-weight: 500;
        opacity: 0.75;
        font-weight: 500;
        opacity: 0.75;
        padding-right: 10px;

        .highlighted {
            //display: inline-block;
            //border-bottom: 1px dashed rgb(48, 48, 48);
            //cursor: pointer;
        }
    }

    .sPropAttrVal {
        position: relative;
        display: flex;
        color: rgb(48, 48, 48);
        background-color: rgb(241, 243, 245);
        font-size: 0.875rem;
        line-height: 1.375rem;
        font-weight: 500;
        opacity: 0.75;
        padding-left: 10px;
    }
}
</style>
